import React, { useState } from 'react';

import { SortableTable } from '~/components/core';
import EmptyState from '~/components/core/EmptyState';
import Text from '~/components/core/TextComponents/Text';
import PencilIcon from '~/components/icons/PencilIcon';
import StarIcon from '~/components/icons/StarIcon';
import InlineIconButton from '~/components/InlineIconButton';
import Collapse from '~/components/ui/atoms/Collapse';

import AgentConfigChip, { AGENT_CHIP_TONE } from '../AgentConfigChip';
import copies from '../copies';
import type { AiExpertScopeConfigurationInfo, AiExpertScopeValues, OrganizationAiExpertInfo } from '../types';
import { isExpertScopeEnabled, isScopeConfigurableExpert } from '../types';

import EditExpertDialog from './EditExpertDialog';

/**
 * The badge the section is headed with, the peer of the conductor's gradient square.
 *
 * Pink rather than the Clive gradient, and a star rather than the conductor's mixer, because the
 * two sections are siblings an operator picks between at a glance — the same distinction the
 * design mock draws. `StarIcon` is the outline star already in the icon set; nothing new is drawn.
 */
const SectionIcon: React.FC = () => (
  <div className="flex shrink-0 items-center justify-center rounded-lg bg-pink-100 p-8">
    <StarIcon size={20} className="text-pink-700" iconColor="currentColor" />
  </div>
);

interface ExpertsCardProps {
  /** The organization's expert catalogue; `isScopeConfigurableExpert` decides which are listed. */
  experts: OrganizationAiExpertInfo[];
  /** This scope's stored rows. An expert with no row does not run here, which is off. */
  scopeRows: AiExpertScopeConfigurationInfo[];
  /**
   * Whether this scope runs the conductor at all. The conductor run mode is unreachable without
   * it -- the conductor is what would do the consulting -- so the dialog disables that option and
   * says why, rather than letting an operator pick a mode that cannot fire.
   */
  isConductorEnabledForScope: boolean;
  /** Persists one expert's whole row and resolves to the refreshed rows, or `undefined` on failure. */
  onSaveExpert: (expertKey: string, values: AiExpertScopeValues) => Promise<unknown>;
}

// SortableTable keys rows by `row.id`; an expert key is already unique within a scope.
interface ExpertTableRow {
  id: string;
  expert: OrganizationAiExpertInfo;
  row?: AiExpertScopeConfigurationInfo;
}

/**
 * The run-mode chips for one expert.
 *
 * Renders the stored pair rather than the narrowed mode, so a row with both booleans set -- which
 * this screen cannot produce, but a direct API write can -- shows as the two things it actually is
 * instead of silently reading as one of them.
 */
const ExpertModeChips: React.FC<{ row?: AiExpertScopeConfigurationInfo }> = ({ row }) => {
  if (!isExpertScopeEnabled(row)) {
    return <AgentConfigChip label={copies.experts.modeOff} tone={AGENT_CHIP_TONE.NEUTRAL} />;
  }

  return (
    <div className="flex items-center gap-4">
      {row?.conductor_enabled ? (
        <AgentConfigChip label={copies.experts.modeConductor} tone={AGENT_CHIP_TONE.ON} />
      ) : null}
      {row?.standalone_enabled ? (
        <AgentConfigChip label={copies.experts.modeStandalone} tone={AGENT_CHIP_TONE.ON} />
      ) : null}
    </div>
  );
};

/**
 * The expert list for one scope, derived from the catalogue rather than authored.
 *
 * Membership is `isScopeConfigurableExpert` -- enabled cross-organization, and not a demo expert,
 * which is authored and configured from the admin catalogue screen instead. Every such expert is
 * listed whether or not it has ever been configured here: there is no create gesture, and the
 * first save is what creates the row.
 */
const ExpertsCard: React.FC<ExpertsCardProps> = ({ experts, scopeRows, isConductorEnabledForScope, onSaveExpert }) => {
  // Keyed on the expert key rather than the object, so the dialog survives a refreshed catalogue.
  const [editingExpertKey, setEditingExpertKey] = useState<string | null>(null);
  // Open by default, like the conductor section it sits beside and the two cards inside it.
  const [isOpen, setIsOpen] = useState(true);

  const configurableExperts = experts.filter(isScopeConfigurableExpert);
  const rowsByExpertKey = new Map(scopeRows.map((row) => [row.expert_key, row]));
  const enabledCount = configurableExperts.filter((expert) =>
    isExpertScopeEnabled(rowsByExpertKey.get(expert.key))
  ).length;
  const editingExpert = configurableExperts.find((expert) => expert.key === editingExpertKey) ?? null;

  const tableRows: ExpertTableRow[] = configurableExperts.map((expert) => ({
    id: expert.key,
    expert,
    row: rowsByExpertKey.get(expert.key),
  }));

  const columns = [
    {
      id: 'display_name',
      label: copies.experts.nameColumn,
      disableSort: true,
      specialCell: (row: ExpertTableRow) => row.expert.display_name,
    },
    {
      id: 'key',
      label: copies.experts.keyColumn,
      disableSort: true,
      specialCell: (row: ExpertTableRow) => (
        <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {row.expert.key}
        </Text>
      ),
    },
    {
      id: 'run_mode',
      label: copies.experts.runModeColumn,
      disableSort: true,
      specialCell: (row: ExpertTableRow) => <ExpertModeChips row={row.row} />,
    },
    {
      id: 'actions',
      label: copies.experts.actionColumn,
      disableSort: true,
      // Right-aligned against the table edge, matching the scope table's actions column.
      align: 'right',
      specialCell: (row: ExpertTableRow) => (
        <InlineIconButton
          icon={PencilIcon}
          tooltipTitle={copies.experts.edit}
          onClick={() => setEditingExpertKey(row.expert.key)}
        />
      ),
    },
  ];

  return (
    <>
      <div className="flex flex-col gap-12 rounded-md border border-solid border-slate-500 p-16">
        {/* The whole header is the expand control, like the conductor section's. A real button, so
            it is keyboard-reachable and states its own collapse. */}
        <button
          type="button"
          onClick={() => setIsOpen((wasOpen) => !wasOpen)}
          aria-expanded={isOpen}
          className="flex cursor-pointer items-center gap-8 border-0 bg-transparent p-0 text-left"
        >
          <SectionIcon />
          {/* Sized as a section, matching Conductor: this card is its peer, not one of the
              sub-cards nested inside it, and rendering it at sub-card size said otherwise. */}
          <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.SEMI_BOLD} className="flex-1">
            {copies.experts.title}
          </Text>
          {/* Kept in the header so the count is still readable while collapsed — it is the one
              thing an operator scanning the two sections wants without opening either. */}
          <AgentConfigChip
            label={copies.experts.count(enabledCount, configurableExperts.length)}
            tone={AGENT_CHIP_TONE.NEUTRAL}
          />
        </button>
        {/* Hidden rather than unmounted, matching the Actions and Stages cards. This card holds no
            fetch of its own — the rows arrive as props — so the choice costs nothing here and keeps
            the three cards behaving the same way. */}
        <Collapse in={isOpen}>
          <div className="flex flex-col gap-12">
            <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
              {copies.experts.helper}
            </Text>
            <SortableTable
              columns={columns}
              rows={tableRows}
              // SortableTable hands the handler the row's *id*, not the row and not an event
              // (`SortableTableCell.handleCellClick` calls `onRowClick(row.id)`), and the id here is
              // the expert key.
              onRowClick={(expertKey: string) => setEditingExpertKey(expertKey)}
              emptyStateComponent={<EmptyState title={copies.experts.empty} />}
            />
          </div>
        </Collapse>
      </div>

      {editingExpert ? (
        <EditExpertDialog
          // Remounted per expert so the markdown editor re-seeds its content, which it does on
          // mount only.
          key={editingExpert.key}
          expert={editingExpert}
          row={rowsByExpertKey.get(editingExpert.key)}
          isConductorEnabledForScope={isConductorEnabledForScope}
          onClose={() => setEditingExpertKey(null)}
          onSave={(values) => onSaveExpert(editingExpert.key, values)}
        />
      ) : null}
    </>
  );
};

export default ExpertsCard;
